import { getOr, isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER, useSplitView } from '../../../../../hooks/useSplitView';
import { useWindowResize } from '../../../../../hooks/useWindowResize';
import { DocumentViewerType, resetPdfText } from '../../../../documents/document-viewer/store';
import { AnalysisView, SelectedDocument } from '../../../../documents/store/types';
import { Analysis, Tabs } from '../../../../shared/analysis/Analysis';
import { AnalysisSideMenu } from '../../../../shared/analysis/AnalysisSideMenu';
import { PageLoadingDots } from '../../../../shared/loading-dots/PageLoadingDots';
import { getFewShotDocument, getFewShotDocumentAnalysisView, getFewShotDocumentUrl, getIsOpening, getSelectedPage, openFewShotDocumentAndQueries, resetFewShotDocument, setFewShotDocumentSelectedPage, toggleDocumentAnalysisView } from '../store';
import styles from './FewShotDocumentAnalysis.module.scss';
import { FewShotQueries } from './FewShotQueries';
import { useFetchStarted } from '../../../../../hooks/useFetchStarted';
import { fetchAllDropdownListsStarted } from '../../../dropdown-lists/store';

export interface FewShotDocumentAnalysisRouteParams {
    agreementTypeId: string | undefined;
    mlFewShotDocumentId: string | undefined;
}

export const FewShotDocumentAnalysis: React.FC<RouteComponentProps<FewShotDocumentAnalysisRouteParams>> = ({ match: { params } }) => {
    const [screenWidth, screenHeight] = useWindowResize();
    const dispatch = useAppDispatch();

    useFetchStarted([fetchAllDropdownListsStarted()]);

    const document = useAppSelector(getFewShotDocument);
    const documentUrl = useAppSelector(getFewShotDocumentUrl);
    const isOpening = useAppSelector(getIsOpening);
    const analysisView = useAppSelector(getFewShotDocumentAnalysisView);
    const selectedPage = useAppSelector(getSelectedPage);
    const [pageWidth, pageHeight] = useSplitView(analysisView, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    const scheduleStartPage = useMemo(() => document?.scheduleStartPage || undefined, [document]);

    const toggleAnalysisView = useCallback((view: AnalysisView) => view !== analysisView && dispatch(toggleDocumentAnalysisView(view)), [dispatch, analysisView]);

    const setSelectedPage = useCallback((page: number | null) => dispatch(setFewShotDocumentSelectedPage(page)), [dispatch]);

    const tabOptions = useMemo<Tabs[]>(() => [
        {
            title: getOr('', 'documentDescription', document),
            onClick: noop,
            selected: true,
            type: 'Few Shot Example'
        },
        {
            title: '',
            onClick: noop,
            selected: false
        }
    ], [document]);

    const analysisWrapperDimensions = { height: `${screenHeight - NAV_BAR - ANALYSIS_MARGIN}px`, width: `${screenWidth}px` };
    const pageDimensions = { height: `${pageHeight}px`, width: `${pageWidth}px` };

    useEffect(() => {
        if (!isUndefined(params.agreementTypeId) && !isUndefined(params.mlFewShotDocumentId) && !isOpening && (!document || document.mlFewShotDocumentId !== parseInt(params.mlFewShotDocumentId))) {
            dispatch(openFewShotDocumentAndQueries(parseInt(params.agreementTypeId), parseInt(params.mlFewShotDocumentId)));
        }
    }, [params, document, dispatch, isOpening]);

    useEffect(() => () => {
        dispatch(resetFewShotDocument());
        dispatch(resetPdfText());
    }, [dispatch]);

    if (isOpening || isNull(document)) {
        return <PageLoadingDots />;
    }

    return (
        <div className={styles.analysisWrapper} style={analysisWrapperDimensions}>
            <AnalysisSideMenu toggleAnalysisView={toggleAnalysisView} currentView={analysisView} isAIPlayground />
            <div className={styles.splitWrapper}>
                {analysisView !== AnalysisView.DATASET &&
                    <div className={styles.documentAnalysisWrapper} style={pageDimensions}>
                        <Analysis
                            selected={document}
                            url={documentUrl}
                            documentInView={SelectedDocument.ORIGINAL}
                            isOpening={isOpening}
                            tabOptions={tabOptions}
                            documentAnalysisView={analysisView}
                            showScheduleStartPage={true}
                            scheduleStartPage={scheduleStartPage}
                            width={pageWidth - 2}
                            type={DocumentViewerType.FEW_SHOT_DOCUMENT}
                            selectedPage={selectedPage}
                            setSelectedPage={setSelectedPage}
                        />
                    </div>
                }
                {analysisView !== AnalysisView.DOCUMENT &&
                    <div className={styles.fewShotAnswersWrapper} style={pageDimensions}>
                        <FewShotQueries fewShotDocument={document} />
                    </div>
                }
            </div>
        </div>
    );
};
